/**
 * Полоса надёжности пароля под полем ввода.
 *
 * Оценка считается в браузере тем же алгоритмом, что и на сервере, поэтому
 * форма не обещает того, что регистрация потом отклонит.
 */
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";

import { MAX_SCORE, evaluate, type Strength } from "../lib/passwordStrength";

interface Props {
  password: string;
  /** Имя и адрес: пароль из собственных данных оценивается ниже. */
  userInputs: string[];
  /** Порог сервера. */
  minScore: number;
  onChange?: (strength: Strength) => void;
}

const COLORS = ["bg-rust", "bg-rust", "bg-amber-500", "bg-amber-400", "bg-sage"];

export function PasswordStrength({ password, userInputs, minScore, onChange }: Props) {
  const { t } = useTranslation();
  const [strength, setStrength] = useState<Strength | null>(null);

  useEffect(() => {
    if (!password) {
      setStrength(null);
      onChange?.({ score: 0, label: "", acceptable: false, hint: null });
      return;
    }

    // Пока считается оценка, поле могли изменить снова: устаревший результат
    // применять нельзя, иначе полоса будет прыгать.
    let current = true;

    void evaluate(password, userInputs, minScore).then((result) => {
      if (!current) return;
      setStrength(result);
      onChange?.(result);
    });

    return () => {
      current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password, minScore, userInputs.join(" ")]);

  if (!password || !strength) return null;

  return (
    <div className="mt-2">
      <div className="flex items-center gap-2">
        <div className="flex flex-1 gap-1" aria-hidden>
          {Array.from({ length: MAX_SCORE }, (_, index) => (
            <span
              key={index}
              className={`h-1 flex-1 rounded-full transition-colors ${
                index < strength.score ? COLORS[strength.score] : "bg-ink-700"
              }`}
            />
          ))}
        </div>

        <span
          className={`w-24 shrink-0 text-right text-xs ${
            strength.acceptable ? "text-sage" : "text-ink-400"
          }`}
        >
          {strength.label}
        </span>
      </div>

      {/* Роль status: изменение читается вслух программами чтения экрана. */}
      <p className="sr-only" role="status">
        {t("password.aria", { label: strength.label })}
      </p>

      {strength.hint && <p className="mt-1.5 text-xs text-ink-400">{strength.hint}</p>}
    </div>
  );
}
